const Config = require("../Config/Config");
const Logger = require("../Logger");
const ConfigurationFactory = require("../ConfigurationFactory");
const Utils = require("../Utils");
const TimelineParser = require("../Parser/TimelineParser");
const LowPerformanceDetection = require("../Performance/LowPerformanceDetection");
const PresentationParser = require("../Parser/PresentationParser");
const ViewerDataInfo = require("../Data/ViewerDataInfo");

const service = {};
const HTML_TEMPLATE_URL = "https://widgets.risevision.com/STAGE/templates/PCODE/src/template.html?cid=CID&id=DID&presentationId=PID&frameElementId=FRAMEID&type=TYPE&viewerId=VIEWERID&applyTimeline=APPLYTIMELINE&isSlowDevice=ISSLOWDEVICE";
const AI_WRAPPER_PC = "8fad9b4ba40a149e29a6c607a24920259d515917";
const HTML_TEMPLATE_DOMAIN = "https://widgets.risevision.com";

/**
 * Giada DN74 is not a forced-rotation device (see ConfigurationFactory.isForcedRotationDevice).
 * Browser viewport and content.json display.orientation can disagree; use display metadata and
 * narrow-by-tall artboard sizing (min/max of reference dimensions) for HTML templates only on DN74.
 */
function isViewportPortraitFromBrowser() {
  try {
    if (typeof window.matchMedia === "function") {
      const mq = window.matchMedia("(orientation: portrait)");
      if (mq && typeof mq.matches === "boolean") {
        return mq.matches;
      }
    }
  } catch (e) {
    /* ignore */
  }

  try {
    if (window.screen && window.screen.orientation && typeof window.screen.orientation.type === "string") {
      return window.screen.orientation.type.indexOf("portrait") !== -1;
    }
  } catch (e) {
    /* WebView may throw on screen.orientation */
  }

  return window.innerWidth < window.innerHeight;
}

/**
 * @returns {boolean|null} portrait when 90/270, landscape when 0/180, null if unavailable
 */
function isPortraitFromDisplayOrientation() {
  const display = ViewerDataInfo.getDisplayData();
  let rot = null;

  if (display && display.orientation != null) {
    rot = Number(display.orientation);
  } else if (typeof window !== "undefined" && window.riseOrientation != null) {
    rot = Number(window.riseOrientation);
  }

  if (rot === null || Number.isNaN(rot)) {
    return null;
  }

  if (rot === 90 || rot === 270) {
    return true;
  }

  if (rot === 0 || rot === 180) {
    return false;
  }

  return null;
}

function getNativeOrientationFromTemplateAttributeData(presentation) {
  if (!presentation || !presentation.templateAttributeData) {
    return null;
  }

  const attrs = JSON.parse(presentation.templateAttributeData || "{}");

  if (attrs.orientation === "portrait" || attrs.orientation === "landscape") {
    return attrs.orientation;
  }

  return null;
}

function getFixedOrientationSettings(presentation) {
  if (!presentation || !presentation.orientationPreset) {
    return {};
  }

  if (["custom", "portrait", "landscape"].includes(presentation.orientationPreset)) {
    if (presentation.orientationPreset === "custom") {
      const w = Number(presentation.widthPreset);
      const h = Number(presentation.heightPreset);
     if (!(w > 0 && h > 0)) {
        return {};
      }
    }
    
    return { 
      orientation: presentation.orientationPreset, 
      width: presentation.widthPreset, 
      height: presentation.heightPreset };
  }

  return {};
}

/**
 * Artboard sizes are passed to createURL and may be axis-swapped in adjustContainersSizes
 * when preserveXY is false (rotation-wrapper / display portrait). Pre-swap so the iframe
 * ends up at the intended dimensions.
 */
function getCustomTemplateDimensions(width, height) {
  const w = Number(width);
  const h = Number(height);

  if (!(w > 0 && h > 0)) {
    return { width: w, height: h };
  }

  if (typeof window === "undefined" ||
    window.isIgnoredRotationDevice ||
    typeof window.isViewportPortrait !== "function" ||
    typeof window.isDisplayPortrait !== "function") {
    return { width: w, height: h };
  }

  const isContentPortrait = w < h;
  const displayVpMatch = window.isViewportPortrait() === window.isDisplayPortrait();
  const contentVpMatch = !window.isDisplayPortrait() && window.isViewportPortrait() === isContentPortrait;
  const preserveXY = displayVpMatch || contentVpMatch;

  if (preserveXY) {
    return { width: w, height: h };
  }

  return { width: h, height: w };
}

function getFixedOrientationTemplateDimensions(fixed) {
  if (!fixed.orientation) {
    return null;
  }

  const windowWidth = ConfigurationFactory.getWindowWidth();
  const windowHeight = ConfigurationFactory.getWindowHeight();

  if (fixed.orientation === "custom") {
    return getCustomTemplateDimensions(fixed.width, fixed.height);
  }

  if (fixed.orientation === "landscape") {
    return getCustomTemplateDimensions(windowWidth, windowHeight);
  }

  if (fixed.orientation === "portrait") {
    return getCustomTemplateDimensions(windowHeight, windowWidth);
  }

  return null;
}

function computeGiadaDn74TemplateDimensions(presentation) {
  const fixed = getFixedOrientationSettings(presentation);
  const fixedDimensions = getFixedOrientationTemplateDimensions(fixed);

  if (fixedDimensions) {
    return fixedDimensions;
  }

  let isPortrait = false;
  let orientationSource = "browser";

  try {
    const orientation = getNativeOrientationFromTemplateAttributeData(presentation);

    if (orientation) {
      isPortrait = orientation === "portrait";
      orientationSource = "templateAttributeData";
    }
  } catch (e) {
    Logger.logDebug(`HTMLTemplateRenderer templateAttributeData: ${e && e.message}`);
  }

  if (orientationSource !== "templateAttributeData") {
    const fromDisplay = isPortraitFromDisplayOrientation();

    if (fromDisplay !== null) {
      isPortrait = fromDisplay;
    } else {
      isPortrait = isViewportPortraitFromBrowser();
    }
  }

  const ww = ConfigurationFactory.getWindowWidth();
  const wh = ConfigurationFactory.getWindowHeight();
  const width = isPortrait ? Math.min(ww, wh) : ww;
  const height = isPortrait ? Math.max(ww, wh) : wh;

  return { width, height };
}

function computeDefaultTemplateDimensions(presentation) {
  const fixed = getFixedOrientationSettings(presentation);
  const fixedDimensions = getFixedOrientationTemplateDimensions(fixed);

  if (fixedDimensions) {
    return fixedDimensions;
  }

  let isPortrait = false;

  try {
    const orientation = getNativeOrientationFromTemplateAttributeData(presentation);

    if (orientation === "portrait") {
      isPortrait = true;
    }
  } catch (e) {
    console.log(e);
  }

  return {
    width: isPortrait ? ConfigurationFactory.getWindowHeight() : ConfigurationFactory.getWindowWidth(),
    height: isPortrait ? ConfigurationFactory.getWindowWidth() : ConfigurationFactory.getWindowHeight()
  };
}

service.render = (presentation, htmlName, containerName) => {
  const { width, height } = ConfigurationFactory.isGiadaDN74()
    ? computeGiadaDn74TemplateDimensions(presentation)
    : computeDefaultTemplateDimensions(presentation);
  const top = 0, left = 0;

  Logger.logDebug(`HTMLTemplateRenderer rendering ${htmlName} into container ${containerName}, presentation: ${JSON.stringify(presentation && presentation.name)}`);

  if (!presentation || !htmlName || !containerName) {
    return;
  }

  const isLegacyTemplate = presentation.layout && presentation.productCode;
  const isWrappedTemplate = presentation.layout && !presentation.productCode;
  const productCode = isWrappedTemplate ? AI_WRAPPER_PC : presentation.productCode;

  let url = HTML_TEMPLATE_URL
    .replace("STAGE", Config.HTML_TEMPLATE_ENV)
    .replace("PCODE", productCode)
    .replace("DID", ConfigurationFactory.getId())
    .replace("CID", ConfigurationFactory.getCompanyId())
    .replace("PID", presentation.id)
    .replace("FRAMEID", "iFrame_" + htmlName)
    .replace("TYPE", ConfigurationFactory.getType())
    .replace("VIEWERID", Utils.getUniqueId())
    .replace("APPLYTIMELINE", TimelineParser.applies() ? "true" : "false")
    .replace("ISSLOWDEVICE", LowPerformanceDetection.isDeviceMarkedAsLowPerformance() ? "true" : "false");

  if (ConfigurationFactory.isSharedschedule()) {
    url += "&env=" + ConfigurationFactory.getEnv(true);
  }

  const viewerHref = Utils.getViewerUrl();
  const riseLitComponentsStagingParam = Utils.getUrlParam(viewerHref, "rlcStage=") || "";
  const commonTemplateStagingParam = Utils.getUrlParam(viewerHref, "ctStage=") || "";
  const riStageParam = Utils.getUrlParam(viewerHref, "riStage=") || "";

  if (riseLitComponentsStagingParam) {
    url += "&rlcStage=" + riseLitComponentsStagingParam;
  }

  if (commonTemplateStagingParam) {
    url += "&ctStage=" + commonTemplateStagingParam;
  }

  if (riStageParam) {
    url += "&riStage=" + riStageParam;
  }

  if (presentation.layout) {
    // Remove any existing data-frame-src attribute baked in by Apps
    presentation.layout = presentation.layout.replace(/\s*data-frame-src="[^"]*"/gi, '');

    // Add data-frame-src attribute to the head tag
    PresentationParser.addDataFrameSrc(presentation, url);

    // Replace rise-init.min.js URL if riStage parameter is present
    if (riStageParam) {
      PresentationParser.replaceRiseInitUrl(presentation, riStageParam);
    }
  }

  if (isLegacyTemplate) {
    window.createTemplate(htmlName, containerName, width, height, presentation.layout);
  } else {
    window.createURL(htmlName, containerName, width, height, top, left, 0, 0, url, true);
  }

  if (isWrappedTemplate) {
    const layout = presentation.layout;
    const iframe = document.getElementById("iFrame_" + htmlName);

    if (iframe) {
      iframe.addEventListener("load", () => {
        iframe.contentWindow.postMessage({ type: "html", html: layout }, HTML_TEMPLATE_DOMAIN);
      });
    }
  }

  RiseVision.Viewer.UI.ViewerZoomController.init(htmlName, width, height);
};

module.exports = service;
